import { Injectable, OnDestroy } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { BehaviorSubject, Subscription } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SnackbarService } from 'services/snackbar.service';

@Injectable({ providedIn: 'root' })
export class NewVersionCheckerService implements OnDestroy {
  private newVersionAvailable$$ = new BehaviorSubject<boolean>(false);
  newVersionAvailable$ = this.newVersionAvailable$$.asObservable();
  private newVersionSubscription?: Subscription;
  private routerSubscription?: Subscription;

  private readonly noReloadRoutePrefixes: string[] = [
    'viewing-passes',
    'exclusive-agreement',
    'plain-agreement',
    'handover-protocols',
    'buying-offer',
    'rental-offer',
    'justimmo',
    'onoffice'
  ];

  constructor(
    private readonly swUpdate: SwUpdate,
    private readonly router: Router,
    private readonly snackbarService: SnackbarService
  ) {
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.checkForUpdate());
  }

  public checkForUpdate(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.newVersionSubscription?.unsubscribe();
    this.newVersionSubscription = this.swUpdate.versionUpdates.subscribe(evt => {
      switch (evt.type) {
        case 'VERSION_DETECTED':
          console.log(`Downloading new app version: ${evt.version.hash}`);
          break;

        case 'VERSION_READY':
          console.log(`Current app version: ${evt.currentVersion.hash}`);
          console.log(`New app version ready for use: ${evt.latestVersion.hash}`);
          this.newVersionAvailable$$.next(true);

          const currentRoute = this.router.url;
          if (this.shouldSkipReload(currentRoute)) {
            console.log('Not auto-reloading due to noReloadRoute.');
            this.showUpdateSnackbar(false);
          } else {
            this.showUpdateSnackbar(true);
          }
          break;

        case 'VERSION_INSTALLATION_FAILED':
          console.error(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
          break;
      }
    });
  }

  private showUpdateSnackbar(allowReload: boolean): void {
    this.snackbarService.showSnackbar(
      'Eine neue Version der App ist verfügbar.',
      'mat-warn',
      false,
      true,
      allowReload ? () => window.location.reload() : undefined,
      allowReload ? 'Neu laden' : undefined
    );
  }

  private shouldSkipReload(route: string): boolean {
    const pattern = new RegExp(`\\/(${this.noReloadRoutePrefixes.join('|')})\\/[a-f0-9]{24}$`);
    return pattern.test(route);
  }

  ngOnDestroy(): void {
    this.newVersionSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }

}
